import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Download, ArrowRight, Beaker, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { FadeUp, FadeIn, StaggerContainer, StaggerItem, ScaleIn } from '../components/ScrollReveal';

const VANILLA_HERO = 'https://customer-assets.emergentagent.com/job_bb23729c-0dea-4683-8280-e0aa97a36711/artifacts/9f2ikqv7_IMG_2754.jpeg';
const VANILLA_BEANS = 'https://customer-assets.emergentagent.com/job_bb23729c-0dea-4683-8280-e0aa97a36711/artifacts/rpuw479q_IMG_2753.jpeg';
const GALLERY_2 = 'https://images.pexels.com/photos/14381803/pexels-photo-14381803.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const CATALOGUE_URL = '/catalogue-vanelys.pdf';
const serif = { fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', fontWeight: 400 };

const Products = () => {
  const { lang } = useLanguage();
  const t = translations[lang].products;
  const common = translations[lang].common;

  // Dictionnaire local pour forcer tes textes exacts tout en gardant le bilinguisme
  const customTexts = {
    fr: {
      vanillaDesc: "Gousses charnues allant de 15 cm à plus de 20 cm",
      origins: [
        { name: 'Madagascar', flag: '🇲🇬' },
        { name: 'Tahiti', flag: '🇵🇫' },
        { name: 'Papouasie-Nouvelle-Guinée', flag: '🇵🇬' },
        { name: 'Indonésie', flag: '🇮🇩' },
        { name: 'Île de la Réunion', flag: '🇷🇪' }
      ],
      brokerageItems: [
        { title: 'Cacao et ses produits dérivés (masse de cacao, poudre de cacao, beurre de cacao et tourteau de cacao)', icon: '🍫' },
        { title: 'Noix de cajou brut', icon: '🥜' }
      ],
      brokerageFooter: "Pour tous produits en lien avec notre activité de courtage, veuillez nous contacter directement pour nous faire part de votre demande"
    },
    en: {
      vanillaDesc: "Fleshy pods ranging from 15 cm to over 20 cm",
      origins: [
        { name: 'Madagascar', flag: '🇲🇬' },
        { name: 'Tahiti', flag: '🇵🇫' },
        { name: 'Papua New Guinea', flag: '🇵🇬' },
        { name: 'Indonesia', flag: '🇮🇩' },
        { name: 'Reunion Island', flag: '🇷🇪' }
      ],
      brokerageItems: [
        { title: 'Cocoa and its derivatives (cocoa mass, cocoa powder, cocoa butter and cocoa cake)', icon: '🍫' },
        { title: 'Raw cashew nuts', icon: '🥜' }
      ],
      brokerageFooter: "For all products related to our brokerage activity, please contact us directly with your request."
    }
  };

  const current = customTexts[lang] || customTexts.fr;
  const vanillaProps = [t.vanillaP1, t.vanillaP2, t.vanillaP3, t.vanillaP4];

  const formats = [
    { key: 'f1', img: VANILLA_HERO },
    { key: 'f2', img: VANILLA_BEANS },
    { key: 'f3', img: GALLERY_2 },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end pb-16 overflow-hidden" data-testid="products-hero">
        <div className="absolute inset-0 z-0">
          <img src={VANILLA_HERO} alt="Vanilla pod" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2C2416]/55" />
        </div>
        <div className="relative z-10 container-max pt-28 text-center w-full">
          <FadeUp>
            <p className="section-overline">{t.overline}</p>
            <h1 className="text-5xl md:text-7xl text-white leading-tight mb-4" style={serif}>
              {t.heroTitle}<br /><em className="not-italic" style={{ color: '#E8D5A0' }}>{t.heroTitleAccent}</em>
            </h1>
            <div className="w-10 h-px bg-[#C9A84C] mx-auto mt-4" />
          </FadeUp>
        </div>
      </section>

      {/* VANILLA SECTION */}
      <section className="section-padding bg-[#FAF7F2]" data-testid="products-vanilla">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <p className="section-overline">{t.vanillaOverline}</p>
              <h2 className="text-4xl md:text-5xl text-[#2C2416] mb-5" style={serif}>{t.vanillaTitle}</h2>
              <div className="gold-divider" />

              {/* MODIFICATION 1 : La nouvelle description */}
              <p className="text-[#7A6E63] text-base font-medium leading-relaxed mt-3 mb-6">
                {current.vanillaDesc}
              </p>

              <ul className="space-y-3 mb-8">
                {vanillaProps.map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-4 h-4 rounded-full border border-[#C9A84C]/60 flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={9} className="text-[#8B4513]" />
                    </div>
                    <span className="text-[#2C2416] text-sm">{p}</span>
                  </li>
                ))}
              </ul>
              <div className="flex gap-4 flex-wrap">
                <Link to="/quote" data-testid="products-quote-btn" className="btn-primary">{common.requestQuote}</Link>
                {/* MODIFICATION 2 : Bouton PDF */}
                <a href={CATALOGUE_URL} download="Catalogue-Vanelys.pdf" target="_blank" rel="noopener noreferrer"
                  data-testid="catalogue-download-btn"
                  className="inline-flex items-center gap-2 text-[#8B4513] hover:text-[#2C2416] text-xs uppercase tracking-widest font-medium transition-colors">
                  <Download size={14} /> {common.catalogueDownload}
                </a>
              </div>
            </FadeUp>
            <ScaleIn delay={0.2}>
              <img src={VANILLA_BEANS} alt="Vanilla beans" className="rounded-2xl w-full h-[450px] object-cover" />
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="section-padding bg-[#F0EBE0]" data-testid="products-formats">
        <div className="container-max">
          <FadeUp>
            <h2 className="text-4xl font-serif text-center text-[#2C2416] mb-12" style={serif}>{t.formatsTitle}</h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-4" staggerDelay={0.1}>
            {formats.map(({ key, img }) => (
              <StaggerItem key={key}>
                <div className="relative rounded-2xl overflow-hidden h-72 group" data-testid={`format-${key}`}>
                  <img src={img} alt={t[`${key}Title`]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2C2416]/80 via-[#2C2416]/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <h3 className="text-xl text-white font-serif mb-1" style={serif}>{t[`${key}Title`]}</h3>
                    <p className="text-white/65 text-xs leading-relaxed">{t[`${key}Desc`]}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ORIGINS */}
      <section className="section-padding bg-[#FAF7F2]" data-testid="products-origins">
        <div className="container-max">
          <FadeUp>
            <h2 className="text-4xl font-serif text-center text-[#2C2416] mb-12" style={serif}>{t.originsTitle}</h2>
          </FadeUp>

          {/* MODIFICATION 3 : Liste des 5 origines exactes sans description bas de texte */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {current.origins.map((origin, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <div className="text-center p-6 border border-[#E5DDD2] rounded-xl hover:border-[#C9A84C]/40 hover:bg-[#F0EBE0] transition-all h-full flex flex-col items-center justify-center">
                  <div className="text-3xl mb-3">{origin.flag}</div>
                  <h3 className="font-serif text-[#2C2416] text-base">{origin.name}</h3>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* BROKERAGE (dark) */}
      <section className="section-padding bg-[#2B2016]" data-testid="products-brokerage">
        <div className="container-max">
          <FadeUp>
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C9A84C] mb-3 text-center">{t.brokerageOverline}</p>
            <h2 className="text-4xl md:text-5xl text-center text-white mb-4" style={serif}>{t.brokerageTitle}</h2>
            <div className="w-10 h-px bg-[#C9A84C] mx-auto my-5" />
            <p className="text-white/50 text-center max-w-xl mx-auto text-sm leading-relaxed mb-12">{t.brokerageDesc}</p>
          </FadeUp>

          {/* MODIFICATION 4 : Les 2 carrés de Cacao et Noix de Cajou */}
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" staggerDelay={0.1}>
            {current.brokerageItems.map((item, i) => (
              <StaggerItem key={i}>
                <div className="border border-white/10 rounded-xl p-8 text-center hover:border-[#C9A84C]/30 transition-all h-full flex flex-col justify-center">
                  <div className="text-4xl mb-5">{item.icon}</div>
                  <h3 className="text-lg text-white mb-2" style={{ fontFamily: '"Cormorant Garamond", serif' }}>{item.title}</h3>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          {/* MODIFICATION 5 : Remplacement de la phrase d'invitation et suppression du bouton PDF */}
          <FadeUp delay={0.2} className="mt-14 text-center max-w-2xl mx-auto">
            <p className="text-white/80 text-base font-light leading-relaxed">
              {current.brokerageFooter}
            </p>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default Products;